/** @format */

import { MIN_TRIP_GROUP_HOURS } from './constants';
import { getPlanningDefaultStart } from './planningDefaultStart';
import { addHoursToTime } from './time';

/** Clamp a trip-slot duration to the API minimum (0.5 h). */
export function minTripGroupHours(hours) {
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return MIN_TRIP_GROUP_HOURS;
  return Math.max(n, MIN_TRIP_GROUP_HOURS);
}

/** Legacy keys still accepted when reading API / stored rows. */
const LEGACY = {
  technician_count: ['nb'],
  duration: ['h'],
  start_time: ['start'],
  end_time: ['end'],
  technician_ids: ['technicianIds', 'technician_ids'],
};

export function readGroupField(group, field) {
  if (!group) return undefined;
  if (group[field] != null && group[field] !== '') return group[field];
  const legacyKey = (LEGACY[field] || []).find(
    (key) => group[key] != null && group[key] !== '',
  );
  return legacyKey != null ? group[legacyKey] : undefined;
}

export function parseGroupHours(value) {
  if (value == null || value === '') return 0;
  return Number(String(value).replace(',', '.')) || 0;
}

export function syncTechnicianIds(ids, technicianCount) {
  const next = [...(ids || [])];
  while (next.length < technicianCount) next.push(null);
  return next.slice(0, technicianCount);
}

/** Slot count from declared effectif and/or technician_ids array length. */
export function groupTechnicianCount(group) {
  const ids = readGroupField(group, 'technician_ids') || [];
  const declared = Number(readGroupField(group, 'technician_count')) || 0;
  return Math.max(declared, ids.length, 1);
}

/** Normalize a group to canonical API field names. */
export function normalizeGroup(group, fallbackDuration = 4, index = 0) {
  const technicianCount = groupTechnicianCount(group);
  const duration = parseGroupHours(
    readGroupField(group, 'duration') ?? fallbackDuration,
  );
  const startTime =
    readGroupField(group, 'start_time') || getPlanningDefaultStart();
  const rawId = readGroupField(group, 'id');
  const id =
    rawId === undefined || rawId === null || rawId === ''
      ? String(index)
      : String(rawId);

  return {
    id,
    technician_count: technicianCount,
    duration,
    start_time: startTime,
    end_time:
      readGroupField(group, 'end_time') || addHoursToTime(startTime, duration),
    technician_ids: syncTechnicianIds(
      readGroupField(group, 'technician_ids'),
      technicianCount,
    ),
  };
}

export function normalizeGroups(groups, fallbackDuration = 4) {
  return (groups || []).map((g, i) => normalizeGroup(g, fallbackDuration, i));
}

/** PATCH / POST body for one group. */
export function groupToApiPayload(group, groupIndex = 0) {
  const normalized = normalizeGroup(group, 4, groupIndex);
  return {
    id: normalized.id,
    technician_count: normalized.technician_count,
    duration: normalized.duration,
    start_time: normalized.start_time,
    end_time: normalized.end_time,
    technician_ids: normalized.technician_ids,
  };
}

/** PATCH payload: technician slots for one group (by id). */
export function groupTechniciansPatch(group, groupIndex = 0) {
  const normalized = normalizeGroup(group, 4, groupIndex);
  return {
    id: normalized.id,
    technician_ids: normalized.technician_ids,
  };
}

export function groupManHours(group) {
  return (
    groupTechnicianCount(group) *
    parseGroupHours(readGroupField(group, 'duration'))
  );
}

export function syncHoursFromGroups(groups) {
  return (groups || []).reduce((sum, g) => sum + groupManHours(g), 0);
}
